/**
 * @file src/ui/react/studio/StartRoomDialog.tsx
 * @description The in-app "Start room" affordance for a collection session — mirrors the website's
 * start-room button, but lives inside the multi-orbiter studio so a session can become a shared room
 * without leaving. On open it mints a room grant for THIS collection (same-origin mycelium call with
 * the session cookie, via `httpClient`) and goes STRAIGHT into the room — the invite link is shown
 * by the waiting room, which is where the people being invited actually arrive. So this dialog is
 * only ever a progress step or a refusal; there is nothing to read once it succeeds.
 *
 * Scope note (follow-up): this only works INSIDE an open collection session (it mints against the
 * loaded collection id). There is no collection PICKER yet for starting a room from nothing.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'plantasia.space-design/react';
import { fetchJsonFromApi } from '../../../api/httpClient.js';
import { getCurrentLanguage } from '../../../i18n/index.js';

// v1 room shape: fixed 2 players / 2 decks. The backend Connect server is the authority; these only
// build the link the orbiter client mints the room from.
const ROOM_PLAYERS = 2;
const ROOM_DECKS = 2;

type Status = 'loading' | 'error';
type TFn = (key: string, opts?: Record<string, unknown>) => string;

export interface StartRoomDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  t: TFn;
}

/**
 * This collection boot URL turned into a room: a creator link (mints, `create=1`) + share link.
 * The room's SHAPE is deliberately absent — it lives on the grant, not on the link. `skipped` rides the creator link only — it counts what the mint left out of the roster
 * because THIS viewer cannot see it, so it is a fact about them, not about the room.
 */
export function buildRoomUrls(roomId: string, grantId: string, skipped = 0): { shareUrl: string; creatorUrl: string } {
  const url = new URL(window.location.href);
  // `code` is in this list although we never write one: the room's name travels with the grant now,
  // and a stale or hand-added `code` on the current URL must not be carried into a room it does not
  // belong to.
  //
  // `performance` is stripped for a different reason, and a sharper one. It is MY device's answer to
  // "can this machine draw the 3D", and in that mode there is no 3D at all — so carrying it into a
  // share link hands whoever opens it a flat interface they never asked for, on hardware that may
  // draw the world perfectly well. Worse, resolving the preference WRITES the url's value to their
  // localStorage, so one shared link would change their default from then on. The preference is
  // per-device and follows its own account; it must never travel on an invitation.
  for (const key of ['room', 'grant', 'players', 'decks', 'create', 'skipped', 'code', 'performance']) {
    url.searchParams.delete(key);
  }
  url.searchParams.set('room', roomId);
  url.searchParams.set('grant', grantId);
  // The SHAPE is not written here. It was recorded on the grant at mint, and the client reads it
  // from there and then from the relay once joined — putting the same two numbers on an editable
  // link only created a second copy that could disagree with the first. Someone arriving by CODE
  // never had them and had to work anyway. The delete above still strips any stale pair.
  const shareUrl = url.toString();
  url.searchParams.set('create', '1');
  if (skipped > 0) url.searchParams.set('skipped', String(skipped));
  const creatorUrl = url.toString();
  return { shareUrl, creatorUrl };
}

export function StartRoomDialog({ open, onOpenChange, collectionId, t }: StartRoomDialogProps) {
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Guards a double mint if the open effect re-runs before the first request settles.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) {
      inFlight.current = false;
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    setErrorMessage(null);
    (async () => {
      try {
        const res = await fetchJsonFromApi('/rooms/grant', {
          method: 'POST',
          body: {
            collectionId,
            // The shape is recorded ON the grant, not only on the link: someone who arrives by
            // saying the code has no link to read these numbers off.
            shape: { decks: ROOM_DECKS, players: ROOM_PLAYERS },
            // The room is named after a living being, in the language of whoever will read that
            // name out — which here is the person pressing Start room.
            lang: getCurrentLanguage(),
          },
        });
        const data = (await res.json().catch(() => ({}))) as {
          grantId?: string; roomId?: string; skipped?: unknown[]; message?: string; error?: string;
        };
        if (res.status === 401) {
          setErrorMessage(t('studio.room.signInRequired'));
          setStatus('error');
          return;
        }
        if (!res.ok || !data?.grantId || !data?.roomId) {
          setErrorMessage(data?.message || data?.error || t('studio.room.generic'));
          setStatus('error');
          return;
        }
        const skipped = Array.isArray(data.skipped) ? data.skipped.length : 0;
        // Into the room. Same tab: the mint is awaited, so a window opened here sits outside the
        // click gesture and gets blocked. `inFlight` is deliberately left set — the tab is leaving,
        // and re-arming the mint under someone already on their way in would mint a second room.
        window.location.assign(buildRoomUrls(String(data.roomId), String(data.grantId), skipped).creatorUrl);
        return;
      } catch {
        setErrorMessage(t('studio.room.generic'));
        setStatus('error');
      } finally {
        // Only reached on a refusal — the success path returns above, leaving the guard armed
        // while the tab navigates.
        inFlight.current = false;
      }
    })();
  }, [open, collectionId, t]);

  const title = status === 'error' ? t('studio.room.errorTitle') : t('studio.room.creatingTitle');
  const description = status === 'error' ? t('studio.room.errorLead') : t('studio.room.creating');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark orb-studio__portal-surface" style={{ maxWidth: '28rem' }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {status === 'error' && errorMessage ? (
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--destructive, #f87171)' }}>{errorMessage}</p>
        ) : null}

      </DialogContent>
    </Dialog>
  );
}
